// import React, { PropsWithChildren } from 'react';
import { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import CategoryIcon from '@material-ui/icons/Category';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import LogoNoBorder from './LogoNoBorder';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import LiveHelpIcon from '@material-ui/icons/LiveHelp';
import AnnouncementIcon from '@material-ui/icons/RecordVoiceOver';
import AssistantIcon from '@material-ui/icons/Assistant';
import FlareIcon from '@material-ui/icons/Flare';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const useNewBadgeStyles = makeStyles({
  badge: {
    backgroundColor: '#3b82f6',
    color: 'white',
    fontSize: '9px',
    fontWeight: 700,
    height: '18px',
    minWidth: '36px',
    borderRadius: '9px',
    padding: '0 6px',
  },
  aiAssistantItem: {
    // Ensure overflow is visible on the link
    overflow: 'visible !important',
    // Target the MuiTypography span directly - this class is stable across environments
    '& .MuiTypography-root': {
      display: 'inline-flex !important',
      alignItems: 'center',
      gap: '8px',
      overflow: 'visible !important',
      '&::after': {
        content: '"NEW"',
        display: 'inline-block',
        backgroundColor: '#3b82f6',
        color: 'white',
        fontSize: '9px',
        fontWeight: 700,
        padding: '3px 8px',
        borderRadius: '9px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        lineHeight: 1.3,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      },
    },
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoNoBorder height={28} /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const badgeClasses = useNewBadgeStyles();

  return (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="/" text="Home" />
        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={GroupIcon}
        />        
        <SidebarItem icon={AnnouncementIcon} to="announcements" text="Announcements" />
        <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
        {/* End global nav */}
        <SidebarDivider />
        <NotificationsSidebarItem />
        <SidebarDivider />        
        <SidebarScrollWrapper>
          <SidebarItem icon={LiveHelpIcon} to="qeta" text="Q&A" />
          <SidebarItem icon={FlareIcon} to="ai-central" text="AI Central" />
          <SidebarItem 
            icon={AssistantIcon} 
            to="ai-assistant" 
            text="AI Assistant"
            className={badgeClasses.aiAssistantItem}
          />
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>
);
};
